import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { X, MapPin } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import AddressAutocomplete, { AddressSuggestion } from './AddressAutocomplete';
import { businessToday } from '../utils/time';
import { byRecentAssignment } from '../utils/people';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * One to-do, from anywhere: the Calendar's day panel, the Field page, My Day, the customer card.
 *
 * Owner, Oct 26 2026: "be able to add a client to a to-do for an address." — "drop the sample bag at 415 Derby
 * Ln". What, the client (optional — the same search the office uses), the address (one of their yards, or
 * typed into the shared address box every intake form uses), a day (today unless picked), a time, who it is
 * for (me / a person / the crew on a route out that day — that last one is his tick, never a default), a note.
 * Paid time (Oct 14) still rides along for the odd shop task. POST /api/todos (services/todos).
 */
type Customer = { id: number; name: string; display_name?: string; address?: string | null; city?: string | null; property_count?: number };
type Property = { id: number; address: string; city?: string | null; label?: string | null };

export default function TodoSheet({ open, onClose, onAdded, date, customer, properties: givenProps, title }: {
  open: boolean;
  onClose: () => void;
  onAdded: (todo: any) => void;
  /** The day it is for; today when left out. */
  date?: string;
  /** Pre-filled from a customer card. */
  customer?: { id: number; name: string } | null;
  properties?: Property[] | null;
  title?: string;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const today = businessToday();
  const [what, setWhat] = useState('');
  const [day, setDay] = useState(date || today);
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const [minutes, setMinutes] = useState('');
  // The client: typed into the search, or given by the card it opened from.
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<Customer[]>([]);
  const [picked, setPicked] = useState<Customer | null>(customer ? { id: customer.id, name: customer.name } : null);
  // The address: one of the client's yards (property_id), or typed/picked in the address box.
  const [propertyId, setPropertyId] = useState<number | 'other' | null>(null);
  const [address, setAddress] = useState('');
  const [addrPick, setAddrPick] = useState<AddressSuggestion | null>(null);
  // Who it is for.
  const [forKind, setForKind] = useState<'me' | 'person' | 'route'>('me');
  const [personId, setPersonId] = useState('');
  const [routeId, setRouteId] = useState('');
  // Paid time (owner, Oct 14 2026): hours that pay the people picked, through the same ledger as a mow.
  const [paidOpen, setPaidOpen] = useState(false);
  const [paidHours, setPaidHours] = useState('');
  const [who, setWho] = useState<Set<number>>(new Set());
  const [done, setDone] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setWhat(''); setDay(date || today); setTime(''); setNote(''); setMinutes('');
    setSearch(''); setHits([]); setPicked(customer ? { id: customer.id, name: customer.name } : null);
    setPropertyId(null); setAddress(''); setAddrPick(null);
    setForKind('me'); setPersonId(''); setRouteId('');
    setPaidOpen(false); setPaidHours(''); setWho(new Set()); setDone((date || today) < today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date, customer?.id]);
  useEffect(() => { setDone(day < today); }, [day, today]);

  // The client's yards: from the card, else looked up once a client is picked.
  const { data: custData } = useQuery(['todo-customer', picked?.id], () => api.get(`/customers/${picked!.id}`).then(r => r.data),
    { enabled: open && !!picked && !givenProps });
  const props: Property[] = givenProps || custData?.properties || [];
  useEffect(() => {
    if (!picked) { setPropertyId(null); return; }
    if (props.length === 1) setPropertyId(props[0].id);
    else if (props.length === 0) setPropertyId('other');
    else setPropertyId(p => (p == null ? props[0].id : p));
  }, [picked, props.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  const { data: people } = useQuery('employees-for-todo', () => api.get('/employees').then(r => (r.data.employees || r.data) as any[]), { enabled: open, staleTime: 300_000 });
  const { data: routesData } = useQuery(['todo-routes', day], () => api.get('/todos/routes', { params: { day } }).then(r => r.data.routes as { id: number; name: string; stops: number }[]), { enabled: open && !!day });
  const routes = routesData || [];
  const members: any[] = (people || []).filter((m: any) => m.is_active !== 0);

  // The client search: the office's own /customers?search=, a keystroke behind.
  useEffect(() => {
    if (!open || picked) return;
    const q = search.trim();
    if (q.length < 2) { setHits([]); return; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/customers', { params: { search: q } });
        if (mine !== seq.current) return;
        setHits(((r.data.customers || []) as Customer[]).slice(0, 6));
      } catch { if (mine === seq.current) setHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, picked]);

  const paying = Number(paidHours) > 0;
  const hasAddress = picked ? (propertyId === 'other' ? !!address.trim() : propertyId != null) : !!address.trim();
  const add = useMutation(() => api.post('/todos', {
    what: what.trim(), day, time: time || undefined, note: note.trim() || undefined, minutes: minutes || undefined,
    customer_id: picked?.id, property_id: picked && propertyId !== 'other' ? propertyId : undefined,
    address: (!picked || propertyId === 'other') ? (addrPick?.label || address.trim() || undefined) : undefined,
    for: forKind, assigned_crew_member_id: forKind === 'person' ? Number(personId) : undefined, route_id: forKind === 'route' ? Number(routeId) : undefined,
    ...(paying ? { paid_hours: Number(paidHours), crew_member_ids: Array.from(who), done } : {}),
  }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'To-do added'); onAdded(d?.todo || null); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not add that'); },
  });

  if (!open) return null;
  const canAdd = !!what.trim() && !!day && !add.isLoading
    && (forKind !== 'person' || !!personId) && (forKind !== 'route' || (!!routeId && hasAddress))
    && (!paying || who.size > 0);
  const names = members.filter(m => who.has(Number(m.id))).map(m => String(m.name).split(' ')[0]);
  const dayWords = day === today ? 'today' : new Date(`${day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900">☐ {title || 'To-do'}</h3>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <input className="input" placeholder="Drop the sample bag, measure the turf, pick up blades…" value={what} onChange={e => setWhat(e.target.value)} autoFocus
          onKeyDown={e => { if (e.key === 'Enter' && canAdd) add.mutate(); }} />

        {/* The client (optional) */}
        <div>
          <p className="text-xs text-gray-600 mb-1">Client <span className="text-gray-500">(optional)</span></p>
          {picked ? (
            <div className="flex items-center justify-between rounded-xl border border-brand-300 bg-brand-50 px-3 py-2">
              <span className="text-sm font-semibold text-brand-900 truncate">{picked.name}</span>
              {!customer && <button type="button" onClick={() => { setPicked(null); setPropertyId(null); setSearch(''); }} className="text-xs text-gray-600 underline">change</button>}
            </div>
          ) : (
            <div>
              <input className="input" placeholder="Search a name, phone or street" value={search} onChange={e => setSearch(e.target.value)} autoComplete="off" />
              {hits.length > 0 && (
                <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden divide-y divide-gray-100">
                  {hits.map(h => (
                    <button key={h.id} type="button" onPointerDown={e => { e.preventDefault(); setPicked({ id: h.id, name: h.display_name || h.name }); setHits([]); }}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-brand-50 flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 truncate">{h.display_name || h.name}</span>
                      {h.property_count != null && <span className="text-[11px] text-gray-500 flex-shrink-0">{h.property_count} {h.property_count === 1 ? 'address' : 'addresses'}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* The address: one of their yards, or typed */}
        <div>
          <p className="text-xs text-gray-600 mb-1">Address {picked ? '' : <span className="text-gray-500">(optional — puts it on the map)</span>}</p>
          {picked && props.length > 0 && (
            <div className="space-y-1 mb-1">
              {props.map(p => (
                <label key={p.id} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer ${propertyId === p.id ? 'border-brand-400 bg-brand-50 text-brand-900 font-semibold' : 'border-gray-200 text-gray-700'}`}>
                  <input type="radio" name="todo-prop" checked={propertyId === p.id} onChange={() => setPropertyId(p.id)} className="text-brand-600" />
                  <MapPin className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                  <span className="truncate">{p.address}{p.city ? `, ${p.city}` : ''}{p.label && !/^(main property|property|home|house)$/i.test(p.label) ? ` · ${p.label}` : ''}</span>
                </label>
              ))}
              <label className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer ${propertyId === 'other' ? 'border-brand-400 bg-brand-50 text-brand-900 font-semibold' : 'border-gray-200 text-gray-700'}`}>
                <input type="radio" name="todo-prop" checked={propertyId === 'other'} onChange={() => setPropertyId('other')} className="text-brand-600" />
                Somewhere else
              </label>
            </div>
          )}
          {(!picked || propertyId === 'other' || props.length === 0) && (
            <AddressAutocomplete value={address} onChange={v => { setAddress(v); setAddrPick(null); }}
              onSelect={s => { setAddress(s.label || s.address); setAddrPick(s); }} placeholder="415 Derby Ln" />
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-gray-600">Day<input type="date" className="input mt-1" value={day} onChange={e => setDay(e.target.value)} /></label>
          <label className="text-xs text-gray-600">Time (optional)<input type="time" className="input mt-1" value={time} onChange={e => setTime(e.target.value)} /></label>
        </div>

        {/* Who it is for */}
        <div>
          <p className="text-xs text-gray-600 mb-1">For</p>
          <div className="flex flex-wrap gap-1.5">
            {([['me', 'Me'], ['person', 'A person'], ['route', 'The crew on a route']] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setForKind(k)}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${forKind === k ? 'border-brand-500 bg-brand-50 text-brand-900' : 'border-gray-200 text-gray-700'}`}>{label}</button>
            ))}
          </div>
          {forKind === 'person' && (
            <select value={personId} onChange={e => setPersonId(e.target.value)} className="input mt-2 text-sm">
              <option value="">Pick a person</option>
              {[...members].sort(byRecentAssignment).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          {forKind === 'route' && (
            routes.length === 0
              ? <p className="text-xs text-amber-700 mt-2">No route is out {dayWords} — pick a day a route runs, or give it to a person.</p>
              : (
                <div className="mt-2 space-y-1">
                  <select value={routeId} onChange={e => setRouteId(e.target.value)} className="input text-sm">
                    <option value="">Pick the route</option>
                    {routes.map(r => <option key={r.id} value={r.id}>{r.name} · {r.stops} stop{r.stops === 1 ? '' : 's'} {dayWords}</option>)}
                  </select>
                  <p className="text-[11px] text-gray-500">Goes on their day as a stop: they see the address and your note on their phone. {hasAddress ? '' : 'It needs an address first.'}</p>
                </div>
              )
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-gray-600">Takes (optional)
            <select value={minutes} onChange={e => setMinutes(e.target.value)} className="input mt-1 text-sm">
              <option value="">—</option>
              {[5, 10, 15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-600">Note (optional)<input className="input mt-1" placeholder="Sample bag is in the truck bed" value={note} onChange={e => setNote(e.target.value)} /></label>
        </div>

        {/* Paid time, folded: the odd shop task that pays somebody by the hour. */}
        <div className="rounded-xl border border-gray-200 p-3 space-y-2">
          <button type="button" onClick={() => setPaidOpen(v => !v)} className="text-xs font-semibold text-gray-600">{paidOpen ? '▾' : '▸'} Paid time (optional)</button>
          {paidOpen && (
            <>
              <input type="number" inputMode="decimal" min={0} step={0.25} className="input" placeholder="Hours, e.g. 1.5" value={paidHours} onChange={e => setPaidHours(e.target.value)} />
              {paying && (
                <>
                  <p className="text-[11px] text-gray-600">Who gets paid for it, at their hourly rate — the same ledger as a mow.</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[...members].sort(byRecentAssignment).map((m: any) => (
                      <label key={m.id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-sm cursor-pointer ${who.has(Number(m.id)) ? 'border-brand-400 bg-brand-50 text-brand-800 font-semibold' : 'border-gray-200 text-gray-700'}`}>
                        <input type="checkbox" checked={who.has(Number(m.id))} className="rounded border-gray-300 text-brand-600"
                          onChange={() => setWho(prev => { const next = new Set(prev); const id = Number(m.id); if (next.has(id)) next.delete(id); else next.add(id); return next; })} />
                        <span className="truncate">{m.name}</span>
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={done} onChange={e => setDone(e.target.checked)} className="rounded border-gray-300 text-brand-600" />
                    Already done — pay it now
                  </label>
                  <p className="text-[11px] text-gray-500">
                    {who.size ? `${paidHours} h to ${names.join(', ')}${done ? ', on that day’s pay' : ', paid when it is marked done'}.` : 'Pick at least one person.'}
                  </p>
                </>
              )}
            </>
          )}
        </div>

        <button onClick={() => add.mutate()} disabled={readOnly || !canAdd} className="w-full py-3 rounded-xl bg-slate-700 text-white font-semibold disabled:opacity-50">
          {add.isLoading ? 'Adding…' : `Add for ${dayWords}`}
        </button>
        <p className="text-[11px] text-gray-500 text-center">Shows on My Day and Needs Attention until it is done, grey on the Calendar{picked ? ', and on their card' : ''}. Nobody is billed.</p>
      </div>
    </div>
  );
}
